// Error texts and the error toast for a failed mutation (`result.errors` of a core service).
// Moved from chawk-bazar / ciseco / jument (`helpers/mutationErrors.js`): the three copies were the same except that
// chawk-bazar / ciseco also take translated texts (`messages`) and can hide the server's Persian text
// (`use_server_message`), which jument did not have. Defaults reproduce all three.
import { toast_configs } from "@core/constants/toast_configs";

// Persian defaults, used when no translated text is given (chawk-bazar, ciseco, jument).
export const DEFAULT_MUTATION_ERROR_MESSAGE = "درخواست شما انجام نشد. لطفاً چند لحظه دیگر دوباره تلاش کنید.";

export const DEFAULT_MUTATION_AUTH_MESSAGE = "برای انجام این کار ابتدا وارد حساب کاربری خود شوید.";

export const DEFAULT_MUTATION_THROTTLE_MESSAGE = "تعداد تلاش‌های شما زیاد است، لطفاً کمی بعد دوباره تلاش کنید.";

// The toast options the three sites used.
export const MUTATION_ERROR_TOAST_OPTIONS = Object.freeze({
	className: "toast-general-style",
	position: "bottom-center",
	autoClose: 4000,
});

// A server message is shown only when it is written in Persian: codes such as `invalid_token` or `required` come
// through the same field and must not reach the user.
const PERSIAN_LETTER = /[؀-ۿ]/;

const user_facing_message = (value) => {
	const text = typeof value === "string" ? value.trim() : "";
	return PERSIAN_LETTER.test(text) ? text : "";
};

// Field errors look like `{ mobile: ["required"], code: "incorrect" }`; forms show them under the input.
export const has_field_errors = (errors) => Object.keys(errors?.[0]?.errors || {}).length > 0;

const first_field_message = (errors) => {
	const field_errors = errors?.[0]?.errors || {};
	for (const value of Object.values(field_errors)) {
		const message = user_facing_message(Array.isArray(value) ? value[0] : value);
		if (message) return message;
	}
	return "";
};

/**
 * The text to show for a failed mutation.
 * Order: 401 → auth text, 403 → throttle text, then the server's Persian message, the first Persian field message,
 * `fallback` (when Persian), `messages.default`, the Persian default.
 * @param errors `result.errors`
 * @param fallback a per-call text (used only when written in Persian, as before)
 * @param options.messages translated texts `{ default, auth, throttle }`
 * @param options.use_server_message false: never show the server's Persian text (a language it is not written in)
 */
export const get_mutation_error_message = (errors, fallback, { messages = {}, use_server_message = true } = {}) => {
	const code = String(errors?.[0]?.code ?? "");
	const texts = messages && typeof messages === "object" ? messages : {};

	if (code === "401") return texts.auth || DEFAULT_MUTATION_AUTH_MESSAGE;
	if (code === "403") return texts.throttle || DEFAULT_MUTATION_THROTTLE_MESSAGE;

	return (
		(use_server_message ? user_facing_message(errors?.[0]?.message) || first_field_message(errors) : "") ||
		user_facing_message(fallback) ||
		texts.default ||
		DEFAULT_MUTATION_ERROR_MESSAGE
	);
};

/**
 * The error toast of a mutation. Can be passed as it is: `error_callback: show_mutation_error_toast`
 * (a second argument that is not an object is ignored).
 * @param errors `result.errors`
 * @param options.message per-call fallback text (see get_mutation_error_message)
 * @param options.messages translated texts `{ default, auth, throttle }`
 * @param options.use_server_message show the server's Persian text (default true)
 * @param options.skip_field_errors no toast when the error is a field error the form already shows
 * @param options.toast_options extra react-toastify options
 * @returns the toast id, or undefined when skipped
 */
export const show_mutation_error_toast = (errors, options = {}) => {
	const {
		message,
		messages,
		use_server_message,
		skip_field_errors = false,
		toast_options = {},
	} = options && typeof options === "object" ? options : {};

	if (skip_field_errors && has_field_errors(errors)) return undefined;

	return toast_configs(get_mutation_error_message(errors, message, { messages, use_server_message }), "error", {
		...MUTATION_ERROR_TOAST_OPTIONS,
		...toast_options,
	});
};
